import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Autocomplete,
  Checkbox,
  Fade,
  Menu,
  MenuItem,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  MovieFilter as MovieFilterIcon,
  Compress as CompressIcon,
  DoneAll as DoneAllIcon,
  DriveFileMove as DriveFileMoveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { surface } from '../theme';
import VideoFootageService from '../services/VideoFootageService';
import TranscriptionService from '../services/TranscriptionService';
import AuthService from '../services/AuthService';
import { useUploadContext } from '../contexts/UploadContext';
import FootageDeleteConfirmDialog from './video-footage/FootageDeleteConfirmDialog';
import MobileListCard from '../components/responsive/MobileListCard';

const PAGE_SIZE = 10;
const UPLOAD_CONCURRENCY = 10;
/** Max how often the upload byte/speed UI re-renders (XHR progress can fire very frequently). */
const UPLOAD_TRANSFER_UI_THROTTLE_MS = 1000;
/** EMA smoothing for speed used in “Time left” only (lower = smoother, slower to react). */
const UPLOAD_ETA_EMA_ALPHA = 0.25;
const ACTIVE_FADE_JOB_STATUSES = new Set(['queued', 'running']);
const TERMINAL_FADE_JOB_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'failed',
]);

const VIDEO_EXT = new Set([
  'mp4',
  'mov',
  'webm',
  'mkv',
  'avi',
  'm4v',
  'wmv',
  'flv',
  'ogv',
  'mpeg',
  'mpg',
]);

function isVideoFile(file) {
  if (file.type && file.type.startsWith('video/')) return true;
  const name = file.name || '';
  const dot = name.lastIndexOf('.');
  if (dot < 0) return false;
  return VIDEO_EXT.has(name.slice(dot + 1).toLowerCase());
}

function formatTimeRemaining(seconds) {
  if (!seconds || seconds === Infinity || Number.isNaN(seconds)) {
    return 'Calculating...';
  }
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function fadeJobStorageKey(folderId) {
  return `video-footage-fade-job:${folderId}`;
}

function isActiveFadeJob(job) {
  return !!job && ACTIVE_FADE_JOB_STATUSES.has(job.status);
}

function fadeJobAction(jobOrAction) {
  return typeof jobOrAction === 'string' ? jobOrAction : jobOrAction?.action || 'fade';
}

function isCompressionJob(jobOrAction) {
  return fadeJobAction(jobOrAction) === 'compress';
}

async function uploadSingleVideoFile(folderId, file, onProgress) {
  const { presignedUrl, objectName, contentType } = await VideoFootageService.requestVideoPresignedUrl(
    folderId,
    {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type || 'video/mp4',
    },
  );
  const ct = contentType || file.type || 'video/mp4';
  const totalSize = file.size;
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', ct);
    if (onProgress) {
      xhr.upload.onprogress = (evt) => {
        const total = evt.lengthComputable && evt.total ? evt.total : totalSize;
        onProgress({ loaded: evt.loaded, total });
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress({ loaded: totalSize, total: totalSize });
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
  await VideoFootageService.completeVideoUpload(folderId, {
    objectName,
    fileSize: file.size,
    fileName: file.name,
    contentType: ct,
  });
}

async function runWithConcurrency(items, limit, worker) {
  if (items.length === 0) return;
  let nextIndex = 0;
  async function runWorker() {
    while (true) {
      const i = nextIndex++;
      if (i >= items.length) return;
      await worker(items[i], i);
    }
  }
  const n = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: n }, () => runWorker()));
}

export default function VideoFootageFolderPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { folderId } = useParams();
  const navigate = useNavigate();
  const { setIsUploading } = useUploadContext();
  const isSuperadmin = AuthService.hasRole('superadmin');
  const canManageFootage = AuthService.hasRole(['admin', 'superadmin']);

  const [folder, setFolder] = useState(null);
  const [folderLoading, setFolderLoading] = useState(true);
  const [folderError, setFolderError] = useState(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [searchTitle, setSearchTitle] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const searchDebounceRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [uploadBatch, setUploadBatch] = useState({ total: 0, success: 0, failed: 0 });
  /** Aggregate byte progress for the current batch (null when idle) */
  const [uploadTransfer, setUploadTransfer] = useState(null);
  /** Shown after a batch finishes; stays until dismissed or leaving the page */
  const [uploadCompleteBanner, setUploadCompleteBanner] = useState(null);
  const [fadeActionRunning, setFadeActionRunning] = useState(false);
  const [fadeActionInfo, setFadeActionInfo] = useState(null);
  const [fadeJob, setFadeJob] = useState(null);
  const [fadeConfirmAction, setFadeConfirmAction] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  const fileInputRef = useRef(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTargetFolder, setMoveTargetFolder] = useState(null);
  const [moveTargetFolders, setMoveTargetFolders] = useState([]);
  const [moveFoldersLoading, setMoveFoldersLoading] = useState(false);
  const [movingVideos, setMovingVideos] = useState(false);
  const [moveError, setMoveError] = useState(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const [mobileMenuItem, setMobileMenuItem] = useState(null);

  const loadFolder = useCallback(async () => {
    try {
      setFolderLoading(true);
      setFolder(null);
      setFolderError(null);
      const data = await VideoFootageService.getFolder(folderId);
      setFolder(data);
    } catch (err) {
      setFolder(null);
      setFolderError(err.response?.data?.error || err.message || 'Failed to load folder');
    } finally {
      setFolderLoading(false);
    }
  }, [folderId]);

  const loadVideos = useCallback(
    async (p, search) => {
      try {
        setLoading(true);
        setError(null);
        const data = await VideoFootageService.listVideos(folderId, {
          page: p + 1,
          search: search?.trim() || undefined,
        });
        setItems(data.items || []);
        setTotal(data.total ?? 0);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load videos');
      } finally {
        setLoading(false);
      }
    },
    [folderId],
  );

  useEffect(() => {
    loadFolder();
  }, [loadFolder]);

  useEffect(() => {
    if (!folder || folderLoading) return;
    loadVideos(page, searchDebounced);
  }, [folder, folderLoading, page, searchDebounced, loadVideos]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchDebounced(searchTitle?.trim() ?? '');
      setPage(0);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTitle]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, searchDebounced]);

  useEffect(() => {
    if (setIsUploading) setIsUploading(uploading);
    return () => {
      if (setIsUploading) setIsUploading(false);
    };
  }, [uploading, setIsUploading]);

  const finishFadeJob = useCallback(
    async (completedJob) => {
      const isCompress = isCompressionJob(completedJob);
      window.localStorage.removeItem(fadeJobStorageKey(folderId));
      setFadeActionInfo(null);
      setFadeJob(null);

      if (completedJob.failed > 0) {
        const firstFailure = completedJob.failures?.[0]?.error;
        setUploadCompleteBanner({
          severity: completedJob.processed > 0 ? 'warning' : 'error',
          message: `${isCompress ? 'Compression' : 'Fade'} complete: ${completedJob.processed} processed, ${completedJob.failed} failed.${firstFailure ? ` First error: ${firstFailure}` : ''}`,
        });
      } else if (completedJob.processed > 0) {
        setUploadCompleteBanner({
          severity: 'success',
          message: `${isCompress ? 'Compression' : 'Fade'} complete: ${completedJob.processed} video${completedJob.processed === 1 ? '' : 's'} processed.`,
        });
      } else {
        setUploadCompleteBanner({
          severity: 'info',
          message: isCompress
            ? 'No videos needed compression.'
            : 'No videos needed fade in/out processing.',
        });
      }

      await loadVideos(page, searchDebounced);
    },
    [folderId, loadVideos, page, searchDebounced],
  );

  useEffect(() => {
    if (!canManageFootage) return undefined;
    const storedJobId = window.localStorage.getItem(fadeJobStorageKey(folderId));
    if (!storedJobId) {
      setFadeJob(null);
      return;
    }

    let cancelled = false;
    VideoFootageService.getFadeJob(folderId, storedJobId)
      .then((job) => {
        if (cancelled) return;
        if (TERMINAL_FADE_JOB_STATUSES.has(job.status)) {
          finishFadeJob(job);
          return;
        }
        setFadeJob(job);
      })
      .catch(() => {
        if (!cancelled) {
          window.localStorage.removeItem(fadeJobStorageKey(folderId));
          setFadeJob(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canManageFootage, folderId, finishFadeJob]);

  useEffect(() => {
    if (!canManageFootage) return undefined;
    if (!fadeJob?.id || !isActiveFadeJob(fadeJob)) return undefined;

    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const nextJob = await VideoFootageService.getFadeJob(folderId, fadeJob.id);
        if (cancelled) return;
        setFadeJob(nextJob);

        if (TERMINAL_FADE_JOB_STATUSES.has(nextJob.status)) {
          window.clearInterval(timer);
          await finishFadeJob(nextJob);
        }
      } catch (err) {
        if (!cancelled) {
          window.localStorage.removeItem(fadeJobStorageKey(folderId));
          setFadeJob(null);
          setFadeActionInfo(null);
          setError(err.response?.data?.error || err.message || 'Failed to load fade progress');
        }
      }
    }, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [canManageFootage, fadeJob, folderId, finishFadeJob]);

  const startBatchUpload = useCallback(
    async (fileList) => {
      const raw = Array.from(fileList || []);
      const videos = raw.filter(isVideoFile);
      const skipped = raw.length - videos.length;

      if (videos.length === 0) {
        setUploadInfo(null);
        setError(skipped > 0 ? 'No video files in selection (skipped non-video files).' : 'No file selected.');
        return;
      }

      setError(null);
      setUploadCompleteBanner(null);
      setUploadInfo(
        skipped > 0 ? `Skipped ${skipped} non-video file${skipped === 1 ? '' : 's'}.` : null,
      );

      const videoCount = videos.length;
      const totalBatchBytes = videos.reduce((sum, f) => sum + f.size, 0);
      setUploadBatch({ total: videoCount, success: 0, failed: 0 });
      setUploading(true);
      setUploadTransfer({ loaded: 0, total: totalBatchBytes, speedMBps: 0, etaSpeedEmaMBps: 0 });

      const items = videos.map((file) => ({ file }));
      let batchSuccess = 0;
      let batchFailed = 0;

      const loadedPerFile = new Array(videoCount).fill(0);
      let lastUiFlushTime = 0;
      let lastUiFlushLoaded = 0;
      const recentSpeeds = [];
      const MAX_SPEED_SAMPLES = 5;
      /** Smoothed MB/s for ETA only; initialized on first valid sample. */
      let etaSpeedEmaMBps = null;

      const reportAggregateProgress = (forceFlush = false) => {
        const aggregateLoaded = loadedPerFile.reduce((a, b) => a + b, 0);
        const now = Date.now();
        if (
          !forceFlush &&
          lastUiFlushTime > 0 &&
          now - lastUiFlushTime < UPLOAD_TRANSFER_UI_THROTTLE_MS
        ) {
          return;
        }
        const elapsedSec = lastUiFlushTime > 0 ? (now - lastUiFlushTime) / 1000 : 0;
        if (elapsedSec > 0 && lastUiFlushTime > 0) {
          const bytesUploaded = aggregateLoaded - lastUiFlushLoaded;
          const speedBytesPerSec = bytesUploaded / elapsedSec;
          const instantaneousSpeed = speedBytesPerSec / (1024 * 1024);
          recentSpeeds.push(instantaneousSpeed);
          if (recentSpeeds.length > MAX_SPEED_SAMPLES) recentSpeeds.shift();
          if (instantaneousSpeed > 0 && Number.isFinite(instantaneousSpeed)) {
            const shouldBlendEma = !forceFlush || etaSpeedEmaMBps == null;
            if (shouldBlendEma) {
              if (etaSpeedEmaMBps == null) {
                etaSpeedEmaMBps = instantaneousSpeed;
              } else {
                etaSpeedEmaMBps =
                  UPLOAD_ETA_EMA_ALPHA * instantaneousSpeed +
                  (1 - UPLOAD_ETA_EMA_ALPHA) * etaSpeedEmaMBps;
              }
            }
          }
        }
        const averageSpeed =
          recentSpeeds.length > 0
            ? recentSpeeds.reduce((acc, s) => acc + s, 0) / recentSpeeds.length
            : 0;
        setUploadTransfer({
          loaded: aggregateLoaded,
          total: totalBatchBytes,
          speedMBps: averageSpeed,
          etaSpeedEmaMBps: etaSpeedEmaMBps ?? 0,
        });
        lastUiFlushLoaded = aggregateLoaded;
        lastUiFlushTime = now;
      };

      try {
        await runWithConcurrency(items, UPLOAD_CONCURRENCY, async ({ file }, fileIndex) => {
          try {
            await uploadSingleVideoFile(folderId, file, ({ loaded }) => {
              loadedPerFile[fileIndex] = loaded;
              reportAggregateProgress(false);
            });
            loadedPerFile[fileIndex] = file.size;
            reportAggregateProgress(true);
            batchSuccess += 1;
            setUploadBatch((prev) => ({ ...prev, success: prev.success + 1 }));
          } catch {
            batchFailed += 1;
            setUploadBatch((prev) => ({ ...prev, failed: prev.failed + 1 }));
          }
        });
        await loadVideos(page, searchDebounced);

        if (batchFailed === 0 && batchSuccess > 0) {
          setUploadCompleteBanner({
            severity: 'success',
            message:
              batchSuccess === 1
                ? '1 video uploaded successfully.'
                : `${batchSuccess} videos uploaded successfully.`,
          });
        } else if (batchSuccess > 0 && batchFailed > 0) {
          setUploadCompleteBanner({
            severity: 'warning',
            message: `Finished: ${batchSuccess} of ${batchSuccess + batchFailed} uploaded successfully.`,
          });
        } else if (batchSuccess === 0 && batchFailed > 0) {
          setError(
            batchFailed === 1
              ? 'Upload failed.'
              : `All ${batchFailed} uploads failed.`,
          );
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Upload failed');
        if (batchSuccess > 0 && batchFailed === 0) {
          setUploadCompleteBanner({
            severity: 'success',
            message:
              batchSuccess === 1
                ? '1 video uploaded successfully.'
                : `${batchSuccess} videos uploaded successfully.`,
          });
        } else if (batchSuccess > 0 && batchFailed > 0) {
          setUploadCompleteBanner({
            severity: 'warning',
            message: `Finished: ${batchSuccess} of ${batchSuccess + batchFailed} uploaded successfully.`,
          });
        }
      } finally {
        setUploading(false);
        setUploadBatch({ total: 0, success: 0, failed: 0 });
        setUploadTransfer(null);
      }
    },
    [folderId, loadVideos, page, searchDebounced],
  );

  const handleFileInputChange = (e) => {
    // Snapshot before clearing: resetting `value` clears the live FileList on the input.
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (!files.length) return;
    startBatchUpload(files);
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading || isActiveFadeJob(fadeJob) || fadeActionRunning || movingVideos) return;
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect =
        uploading || isActiveFadeJob(fadeJob) || fadeActionRunning || movingVideos ? 'none' : 'copy';
    }
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragActive(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);
    if (uploading || isActiveFadeJob(fadeJob) || fadeActionRunning || movingVideos) return;
    if (e.dataTransfer?.files?.length) startBatchUpload(e.dataTransfer.files);
  };

  const handlePlay = async (video) => {
    try {
      const { url } = await VideoFootageService.getPlayUrl(folderId, video.id);
      setPreviewTitle(video.fileName || 'Video');
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not load video');
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl('');
    setPreviewTitle('');
  };

  const handleDeleteClick = (item, e) => {
    e.stopPropagation();
    if (selectedIds.size > 0 && selectedIds.has(item.id)) {
      const payloadItems = items
        .filter((i) => selectedIds.has(i.id))
        .map((i) => ({
          id: i.id,
          fileName: i.fileName,
          fileSize: i.fileSize,
        }));
      setDeleteItem({ items: payloadItems });
    } else {
      setDeleteItem({
        id: item.id,
        fileName: item.fileName,
        fileSize: item.fileSize,
      });
    }
  };

  const handleDeleteClose = () => {
    if (!deleting) setDeleteItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;
    try {
      setDeleting(true);
      if (deleteItem.items) {
        const results = await Promise.allSettled(
          deleteItem.items.map((it) => VideoFootageService.deleteVideo(folderId, it.id)),
        );
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length > 0) {
          const first = failed[0].reason;
          const msg = first?.response?.data?.error || first?.message || 'Failed to delete';
          setError(
            failed.length === deleteItem.items.length
              ? msg
              : `${deleteItem.items.length - failed.length} deleted; ${failed.length} failed: ${msg}`,
          );
        }
      } else {
        await VideoFootageService.deleteVideo(folderId, deleteItem.id);
      }
      setDeleteItem(null);
      setSelectedIds(new Set());
      await loadVideos(page, searchDebounced);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleApplyFadeInOut = async () => {
    try {
      setFadeActionRunning(true);
      setFadeConfirmAction(null);
      setError(null);
      setUploadCompleteBanner(null);
      const job = await VideoFootageService.applyFadeInOutToMissingVideos(folderId);
      setFadeJob(job);
      if (TERMINAL_FADE_JOB_STATUSES.has(job.status)) {
        await finishFadeJob(job);
      } else {
        window.localStorage.setItem(fadeJobStorageKey(folderId), job.id);
        setFadeActionInfo('Fade job started. You can navigate away; processing will continue on the server.');
      }
    } catch (err) {
      setError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to apply fades');
    } finally {
      setFadeActionRunning(false);
    }
  };

  const handleCompressVideos = async () => {
    try {
      setFadeActionRunning(true);
      setFadeConfirmAction(null);
      setError(null);
      setUploadCompleteBanner(null);
      const job = await VideoFootageService.compressVideos(folderId);
      setFadeJob(job);
      if (TERMINAL_FADE_JOB_STATUSES.has(job.status)) {
        await finishFadeJob(job);
      } else {
        window.localStorage.setItem(fadeJobStorageKey(folderId), job.id);
        setFadeActionInfo('Compression job started. You can navigate away; processing will continue on the server.');
      }
    } catch (err) {
      setError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to compress videos');
    } finally {
      setFadeActionRunning(false);
    }
  };

  const handleMarkAllFadeInOut = async () => {
    try {
      setFadeActionRunning(true);
      setFadeConfirmAction(null);
      setError(null);
      const result = await VideoFootageService.markAllFadeInOut(folderId);
      setUploadCompleteBanner({
        severity: 'success',
        message: `Marked ${result.modified ?? 0} of ${result.matched ?? 0} videos as faded.`,
      });
      await loadVideos(page, searchDebounced);
    } catch (err) {
      setError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to mark videos');
    } finally {
      setFadeActionRunning(false);
    }
  };

  const loadMoveTargetFolders = async () => {
    try {
      setMoveFoldersLoading(true);
      const data = await VideoFootageService.listFolders({ all: true });
      const folders = (data.items || []).filter((f) => f.id !== folderId);
      setMoveTargetFolders(folders);
      setMoveTargetFolder((prev) =>
        prev && folders.some((f) => f.id === prev.id) ? prev : null,
      );
    } catch (err) {
      setMoveError(err.response?.data?.error || err.message || 'Failed to load folders');
      setMoveTargetFolders([]);
    } finally {
      setMoveFoldersLoading(false);
    }
  };

  const openMoveDialog = () => {
    setMoveDialogOpen(true);
    setMoveError(null);
    setMoveTargetFolder(null);
    loadMoveTargetFolders();
  };

  const closeMoveDialog = () => {
    if (movingVideos) return;
    setMoveDialogOpen(false);
    setMoveError(null);
    setMoveTargetFolder(null);
  };

  const confirmMoveAllVideos = async () => {
    if (!moveTargetFolder) {
      setMoveError('Choose a destination folder');
      return;
    }

    try {
      setMovingVideos(true);
      setMoveError(null);
      setError(null);
      setUploadCompleteBanner(null);
      const result = await VideoFootageService.moveAllVideos(folderId, moveTargetFolder.id);
      setMoveDialogOpen(false);
      setMoveTargetFolder(null);
      setSelectedIds(new Set());
      setPage(0);
      await loadVideos(0, searchDebounced);
      const failed = result.failed ?? 0;
      setUploadCompleteBanner({
        severity: failed > 0 ? 'warning' : 'success',
        message:
          failed > 0
            ? `Moved ${result.moved ?? 0} of ${result.total ?? 0} videos to ${result.destinationFolder?.name || moveTargetFolder.name}.`
            : `Moved ${result.moved ?? 0} videos to ${result.destinationFolder?.name || moveTargetFolder.name}.`,
      });
    } catch (err) {
      setMoveError(
        err.response?.data?.details ||
          err.response?.data?.error ||
          err.message ||
          'Failed to move videos',
      );
    } finally {
      setMovingVideos(false);
    }
  };

  const closeFadeConfirm = () => {
    if (!fadeActionRunning) setFadeConfirmAction(null);
  };

  const confirmFadeAction = () => {
    if (fadeConfirmAction === 'apply') {
      handleApplyFadeInOut();
      return;
    }
    if (fadeConfirmAction === 'compress') {
      handleCompressVideos();
      return;
    }
    if (fadeConfirmAction === 'mark') {
      handleMarkAllFadeInOut();
    }
  };

  const openMobileMenu = (event, item) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(event.currentTarget);
    setMobileMenuItem(item);
  };
  const closeMobileMenu = () => {
    setMobileMenuAnchorEl(null);
    setMobileMenuItem(null);
  };

  if (!AuthService.hasRole(['uploader', 'admin', 'superadmin'])) {
    return <Navigate to='/' replace />;
  }

  if (folderLoading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (folderError || !folder) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/video-footage')} sx={{ mb: 2 }}>
          Back to folders
        </Button>
        <Alert severity='error'>{folderError || 'Folder not found'}</Alert>
      </Box>
    );
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fadeJobActive = isActiveFadeJob(fadeJob);
  const fadeOperationActive = fadeActionRunning || fadeJobActive;
  const folderOperationActive = fadeOperationActive || movingVideos;
  const fadeJobCompleted = fadeJob?.completed ?? ((fadeJob?.processed || 0) + (fadeJob?.failed || 0));
  const fadeJobProgressPct =
    fadeJob && fadeJob.total > 0
      ? Math.min(100, Math.round((fadeJobCompleted / fadeJob.total) * 100))
      : 0;
  const fadeJobIsCompressing = isCompressionJob(fadeJob);
  const { total: uploadTotalCount, success: uploadOkCount, failed: uploadFailedCount } = uploadBatch;
  const uploadSettledCount = uploadOkCount + uploadFailedCount;
  const uploadProgressPct =
    uploadTotalCount > 0 ? Math.round((uploadSettledCount / uploadTotalCount) * 100) : 0;
  const uploadBytePct =
    uploadTransfer && uploadTransfer.total > 0
      ? Math.min(100, (uploadTransfer.loaded / uploadTransfer.total) * 100)
      : 0;
  const showDropFeedback = dragActive && !uploading && !folderOperationActive;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/video-footage')}
        sx={{ mb: 2, color: 'white' }}
      >
        Back to folders
      </Button>
      <Typography
        variant='h4'
        sx={{
          fontWeight: 700,
          color: 'white',
          mb: 1,
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        {folder.name}
      </Typography>
      {isSuperadmin && (
        <Typography variant='body2' color='rgba(255,255,255,0.85)' sx={{ mb: 3 }}>
          Owners:{' '}
          {(folder.owners || [])
            .map((o) => o.displayName || o.username)
            .filter(Boolean)
            .join(', ') || '—'}
        </Typography>
      )}

      <Paper sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
        <Box
          sx={(theme) => ({
            p: 3,
            borderRadius: 2,
            border: showDropFeedback ? '2px dashed' : '2px solid transparent',
            borderColor: showDropFeedback ? 'primary.main' : 'transparent',
            bgcolor: showDropFeedback ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
            boxShadow: showDropFeedback
              ? `${theme.shadows[6]}, 0 0 0 3px ${alpha(theme.palette.primary.main, 0.22)}`
              : 'none',
            transform: showDropFeedback ? 'scale(1.006)' : 'scale(1)',
            transition:
              'border-color 0.26s ease-out, background-color 0.26s ease-out, box-shadow 0.26s ease-out, transform 0.26s ease-out',
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
              transform: 'none',
            },
          })}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          {error && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {uploadInfo && (
            <Alert severity='info' sx={{ mb: 2 }} onClose={() => setUploadInfo(null)}>
              {uploadInfo}
            </Alert>
          )}
          {uploadCompleteBanner && (
            <Alert
              severity={uploadCompleteBanner.severity}
              sx={{ mb: 2 }}
              onClose={() => setUploadCompleteBanner(null)}
            >
              {uploadCompleteBanner.message}
            </Alert>
          )}
          {fadeActionInfo && (
            <Alert severity='info' sx={{ mb: 2 }}>
              {fadeActionInfo}
            </Alert>
          )}
          {fadeJobActive && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='body2' sx={{ mb: 1, fontWeight: 600 }}>
                {fadeJobIsCompressing ? 'Compressing videos' : 'Applying 1s fades'}: {fadeJobCompleted} of {fadeJob.total} videos processed
                {fadeJob.failed > 0 ? (
                  <Typography component='span' color='error' sx={{ ml: 1, fontWeight: 600 }}>
                    ({fadeJob.failed} failed)
                  </Typography>
                ) : null}
              </Typography>
              <LinearProgress
                variant={fadeJob.total > 0 ? 'determinate' : 'indeterminate'}
                value={fadeJobProgressPct}
                sx={{ borderRadius: 1, height: 8 }}
              />
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                {fadeJob.currentFileName ? `Current: ${fadeJob.currentFileName}. ` : ''}
                You can navigate away; processing will continue on the server.
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
            <input
              ref={fileInputRef}
              type='file'
              accept='video/*'
              multiple
              disabled={uploading || folderOperationActive}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            <Button
              type='button'
              variant='contained'
              startIcon={uploading ? <CircularProgress size={20} color='inherit' /> : <CloudUploadIcon />}
              disabled={uploading || folderOperationActive}
              onClick={() => {
                if (!uploading && !folderOperationActive) fileInputRef.current?.click();
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {uploading
                ? `Uploading… ${uploadOkCount} of ${uploadTotalCount} uploaded`
                : 'Upload videos'}
            </Button>
            {canManageFootage && (
              <>
                <Button
                  type='button'
                  variant='outlined'
                  startIcon={fadeActionRunning ? <CircularProgress size={18} /> : <MovieFilterIcon />}
                  disabled={uploading || folderOperationActive || total === 0}
                  onClick={() => setFadeConfirmAction('apply')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Apply 1s fades
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  startIcon={fadeActionRunning ? <CircularProgress size={18} /> : <CompressIcon />}
                  disabled={uploading || folderOperationActive || total === 0}
                  onClick={() => setFadeConfirmAction('compress')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Compress videos
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  startIcon={<DoneAllIcon />}
                  disabled={uploading || folderOperationActive || total === 0}
                  onClick={() => setFadeConfirmAction('mark')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Mark all faded
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  startIcon={movingVideos ? <CircularProgress size={18} /> : <DriveFileMoveIcon />}
                  disabled={uploading || folderOperationActive || loading}
                  onClick={openMoveDialog}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  {movingVideos ? 'Moving videos...' : 'Move all videos'}
                </Button>
              </>
            )}
            <Typography variant='body2' color='text.secondary' sx={{ flex: '1 1 200px' }}>
              {folderOperationActive
                ? movingVideos
                  ? 'Moving videos to another folder.'
                  : 'Wait for the current video action to finish.'
                : uploading
                ? 'Wait for the current upload to finish before adding more files.'
                : 'Drop video files anywhere in this panel, or use the button.'}
            </Typography>
            <TextField
              size='small'
              placeholder='Search by file name'
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 0, width: { xs: '100%', sm: 240 } }}
            />
          </Box>

          <Fade in={showDropFeedback} timeout={280} unmountOnExit>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudUploadIcon color='primary' sx={{ fontSize: 22, opacity: 0.95 }} />
              <Typography variant='subtitle2' color='primary' sx={{ fontWeight: 700 }}>
                Release to add videos
              </Typography>
            </Box>
          </Fade>

          {uploading && uploadTotalCount > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='body2' sx={{ mb: 1, fontWeight: 600 }}>
                Uploaded {uploadOkCount} of {uploadTotalCount} files
                {uploadFailedCount > 0 ? (
                  <Typography component='span' color='error' sx={{ ml: 1, fontWeight: 600 }}>
                    ({uploadFailedCount} failed)
                  </Typography>
                ) : null}
              </Typography>
              <LinearProgress variant='determinate' value={uploadProgressPct} sx={{ borderRadius: 1, height: 8 }} />
              {uploadTransfer && uploadTransfer.total > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant='body2' sx={{ mb: 1, fontWeight: 600 }}>
                    Data: {TranscriptionService.formatFileSize(uploadTransfer.loaded)} /{' '}
                    {TranscriptionService.formatFileSize(uploadTransfer.total)}
                  </Typography>
                  <LinearProgress variant='determinate' value={uploadBytePct} sx={{ borderRadius: 1, height: 8 }} />
                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      typography: 'body2',
                      color: 'text.secondary',
                    }}
                  >
                    <span>
                      Speed:{' '}
                      {uploadTransfer.speedMBps > 0
                        ? `${uploadTransfer.speedMBps.toFixed(2)} MB/s`
                        : '—'}
                    </span>
                    <span>
                      Time left:{' '}
                      {uploadTransfer.etaSpeedEmaMBps > 0 && uploadTransfer.total > 0
                        ? formatTimeRemaining(
                            (uploadTransfer.total - uploadTransfer.loaded) /
                              (uploadTransfer.etaSpeedEmaMBps * 1024 * 1024),
                          )
                        : 'Calculating...'}
                    </span>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {loading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography color='text.secondary' sx={{ py: 3 }}>
              No videos in this folder yet. Upload a video to get started.
            </Typography>
          ) : isMobile ? (
            <>
              {items.map((item) => (
                <MobileListCard
                  key={item.id}
                  title={item.fileName}
                  subtitle={item.uploadedBy?.displayName || item.uploadedBy?.username || '—'}
                  meta={item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                  primaryAction={
                    <Button size='small' onClick={() => handlePlay(item)} sx={{ minWidth: 0, px: 1 }}>
                      Play
                    </Button>
                  }
                  secondaryAction={
                    canManageFootage ? (
                      <IconButton size='small' onClick={(e) => openMobileMenu(e, item)}>
                        <MoreVertIcon fontSize='small' />
                      </IconButton>
                    ) : null
                  }
                >
                  <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    <Chip
                      size='small'
                      color={item.hasFadeInFadeOut ? 'success' : 'default'}
                      label={item.hasFadeInFadeOut ? 'Faded' : 'No fades'}
                    />
                    {item.isCompressed && (
                      <Chip size='small' color='primary' label='Compressed' />
                    )}
                  </Box>
                  <Typography variant='body2' color='text.secondary'>
                    {item.fileSize != null ? `${(item.fileSize / (1024 * 1024)).toFixed(2)} MB` : '—'}
                  </Typography>
                </MobileListCard>
              ))}
              {canManageFootage && (
                <Menu anchorEl={mobileMenuAnchorEl} open={Boolean(mobileMenuAnchorEl)} onClose={closeMobileMenu}>
                <MenuItem
                  onClick={() => {
                    if (mobileMenuItem) handlePlay(mobileMenuItem);
                    closeMobileMenu();
                  }}
                >
                  Play
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (!mobileMenuItem) return;
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(mobileMenuItem.id)) next.delete(mobileMenuItem.id);
                      else next.add(mobileMenuItem.id);
                      return next;
                    });
                    closeMobileMenu();
                  }}
                >
                  Toggle selection
                </MenuItem>
                <MenuItem
                  sx={{ color: 'error.main' }}
                  onClick={(e) => {
                    if (mobileMenuItem) handleDeleteClick(mobileMenuItem, e);
                    closeMobileMenu();
                  }}
                >
                  Delete
                </MenuItem>
                </Menu>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                <Pagination
                  count={pageCount}
                  page={page + 1}
                  onChange={(_, value) => setPage(value - 1)}
                  color='primary'
                  size='small'
                />
              </Box>
            </>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {canManageFootage && (
                        <TableCell padding='checkbox' sx={{ fontWeight: 700 }}>
                          <Checkbox
                            size='small'
                            indeterminate={
                              items.some((i) => selectedIds.has(i.id)) &&
                              !items.every((i) => selectedIds.has(i.id))
                            }
                            checked={items.length > 0 && items.every((i) => selectedIds.has(i.id))}
                            onChange={() => {
                              const ids = items.map((i) => i.id);
                              setSelectedIds((prev) => {
                                const allSelected =
                                  ids.length > 0 && ids.every((id) => prev.has(id));
                                const next = new Set(prev);
                                if (allSelected) {
                                  ids.forEach((id) => next.delete(id));
                                } else {
                                  ids.forEach((id) => next.add(id));
                                }
                                return next;
                              });
                            }}
                            inputProps={{ 'aria-label': 'Select all on this page' }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 700 }}>File name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Size</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fade</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Uploaded by</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id} hover>
                        {canManageFootage && (
                          <TableCell padding='checkbox' onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size='small'
                              checked={selectedIds.has(item.id)}
                              onChange={() => {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(item.id)) next.delete(item.id);
                                  else next.add(item.id);
                                  return next;
                                });
                              }}
                              inputProps={{ 'aria-label': `Select ${item.fileName || 'video'}` }}
                            />
                          </TableCell>
                        )}
                        <TableCell>{item.fileName}</TableCell>
                        <TableCell>
                          {item.fileSize != null
                            ? `${(item.fileSize / (1024 * 1024)).toFixed(2)} MB`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            <Chip
                              size='small'
                              color={item.hasFadeInFadeOut ? 'success' : 'default'}
                              label={item.hasFadeInFadeOut ? 'Faded' : 'No fades'}
                            />
                            {item.isCompressed && (
                              <Chip size='small' color='primary' label='Compressed' />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {item.uploadedBy?.displayName || item.uploadedBy?.username || '—'}
                        </TableCell>
                        <TableCell>
                          {item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {canManageFootage && selectedIds.size > 0 && selectedIds.has(item.id) ? (
                            <Tooltip
                              title={
                                selectedIds.size > 1
                                  ? `Delete ${selectedIds.size} selected videos`
                                  : 'Delete'
                              }
                            >
                              <IconButton
                                size='small'
                                color='error'
                                onClick={(e) => handleDeleteClick(item, e)}
                              >
                                <DeleteIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <>
                              <Tooltip title='Play'>
                                <IconButton
                                  size='small'
                                  color='primary'
                                  onClick={() => handlePlay(item)}
                                >
                                  <PlayArrowIcon />
                                </IconButton>
                              </Tooltip>
                              {canManageFootage && (
                                <Tooltip title='Delete'>
                                  <IconButton
                                    size='small'
                                    color='error'
                                    onClick={(e) => handleDeleteClick(item, e)}
                                  >
                                    <DeleteIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  {total === 0 ? 0 : page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of{' '}
                  {total}
                </Typography>
                <Pagination
                  count={pageCount}
                  page={page + 1}
                  onChange={(_, value) => setPage(value - 1)}
                  color='primary'
                  size='small'
                  siblingCount={2}
                  boundaryCount={1}
                  showFirstButton
                  showLastButton
                />
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <Dialog
        open={Boolean(fadeConfirmAction)}
        onClose={closeFadeConfirm}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {fadeConfirmAction === 'apply'
            ? 'Apply 1s fades?'
            : fadeConfirmAction === 'compress'
            ? 'Compress videos?'
            : 'Mark all videos as faded?'}
        </DialogTitle>
        <DialogContent>
          <Typography color='text.secondary'>
            {fadeConfirmAction === 'apply'
              ? 'This will use ffmpeg to add a 1-second fade in and 1-second fade out to every video in this folder that is not already marked as faded. Existing stored video files will be overwritten. You can navigate away after starting; processing will continue on the server.'
              : fadeConfirmAction === 'compress'
              ? 'This will use ffmpeg to compress every video in this folder that is not already marked as compressed. Existing stored video files will be overwritten. You can navigate away after starting; processing will continue on the server.'
              : 'This only updates metadata. It will not edit the stored video files.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFadeConfirm} disabled={fadeActionRunning}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color={fadeConfirmAction === 'mark' ? 'warning' : 'primary'}
            onClick={confirmFadeAction}
            disabled={fadeActionRunning}
            startIcon={fadeActionRunning ? <CircularProgress size={18} color='inherit' /> : null}
          >
            {fadeConfirmAction === 'apply'
              ? 'Apply fades'
              : fadeConfirmAction === 'compress'
              ? 'Compress videos'
              : 'Mark all faded'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveDialogOpen} onClose={closeMoveDialog} maxWidth='sm' fullWidth>
        <DialogTitle>Move all videos</DialogTitle>
        <DialogContent>
          <Typography color='text.secondary' sx={{ mb: 2 }}>
            Move every video in this folder to another footage folder. This affects all pages, not just the visible list.
          </Typography>
          {moveError && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setMoveError(null)}>
              {moveError}
            </Alert>
          )}
          <Autocomplete
            disabled={movingVideos}
            loading={moveFoldersLoading}
            options={moveTargetFolders}
            value={moveTargetFolder}
            onChange={(_, value) => setMoveTargetFolder(value)}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Destination folder'
                placeholder='Select a folder'
                required
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMoveDialog} disabled={movingVideos}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={confirmMoveAllVideos}
            disabled={movingVideos || !moveTargetFolder}
            startIcon={movingVideos ? <CircularProgress size={18} color='inherit' /> : null}
          >
            Move videos
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={closePreview} maxWidth='md' fullWidth>
        <DialogTitle>{previewTitle}</DialogTitle>
        <DialogContent>
          {previewUrl && (
            <Box sx={{ mt: 1, width: '100%' }}>
              <video src={previewUrl} controls style={{ width: '100%', maxHeight: '70vh' }} playsInline />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      <FootageDeleteConfirmDialog
        mode='videos'
        deleteItem={deleteItem}
        deleting={deleting}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}
